import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { compact, first, isEmpty, map } from "lodash/fp";

import { isResponseErrorWithStatus } from "@readai/common/utils";

import * as api from "@app/booking/api";
import ROUTES from "@app/booking/routes";
import useSchedulingStore from "@app/booking/stores/scheduling";
import type { PublicRssLink } from "@app/booking/types/rssLinkTypes";

const EMPTY_LINKS: PublicRssLink[] = [];

const usePublicRssLinks = (ownerVanityId?: string, linkId?: string) => {
  const navigate = useNavigate();
  const [isDisabled, setIsDisabled] = useState(false);
  const setConstraints = useSchedulingStore((state) => state.setConstraints);

  const listPublicRssLinksQuery = useQuery({
    queryKey: ["booking", ownerVanityId],
    queryFn: async () => {
      if (!ownerVanityId) return EMPTY_LINKS;

      const response = await api.rssLinks.listPublicRssLinks(ownerVanityId);
      return isEmpty(response) ? EMPTY_LINKS : response;
    },
    enabled: Boolean(ownerVanityId) && !linkId,
    retry: (failureCount, error) =>
      !isResponseErrorWithStatus(error, [403, 404]) && failureCount < 3,
  });

  const getPublicRssLinkQuery = useQuery({
    queryKey: ["booking", ownerVanityId, linkId],
    queryFn: async () => {
      if (!ownerVanityId || !linkId) return null;
      return api.rssLinks.getPublicRssLink(ownerVanityId, linkId);
    },
    enabled: Boolean(ownerVanityId) && Boolean(linkId),
    retry: (failureCount, error) =>
      !isResponseErrorWithStatus(error, [403, 404]) && failureCount < 3,
  });

  // data could be empty if the user has all their default links disabled
  const hasNoEnabledLinks =
    listPublicRssLinksQuery.isSuccess && isEmpty(listPublicRssLinksQuery.data);

  useEffect(() => {
    if (hasNoEnabledLinks) {
      navigate(ROUTES.NOT_FOUND);
    }
  }, [hasNoEnabledLinks]);

  const isError =
    getPublicRssLinkQuery.isError || listPublicRssLinksQuery.isError;

  useEffect(() => {
    if (!isError) return;

    const error = (getPublicRssLinkQuery.error ??
      listPublicRssLinksQuery.error) as unknown;

    if (isResponseErrorWithStatus(error, 403)) {
      setIsDisabled(true);
    } else if (isResponseErrorWithStatus(error, 404)) {
      navigate(ROUTES.NOT_FOUND);
    } else {
      Sentry.captureException(error);
      navigate(ROUTES.ERROR);
    }
  }, [isError]);

  const constrainedLink = getPublicRssLinkQuery.data;
  const allLinks = listPublicRssLinksQuery.data ?? EMPTY_LINKS;

  useEffect(() => {
    if (!constrainedLink) return;

    setConstraints(constrainedLink);
  }, [setConstraints, constrainedLink?.vanityId]);

  const ownerName = useMemo(
    () =>
      constrainedLink?.ownerName ?? first(compact(map("ownerName", allLinks))),
    [constrainedLink, allLinks.length],
  );

  return {
    isDisabled,
    isError,
    isLoading:
      listPublicRssLinksQuery.isLoading || getPublicRssLinkQuery.isLoading,

    allLinks,
    link: constrainedLink,
    ownerName,
  };
};

export default usePublicRssLinks;
